<script>
/**
 * Xero venues
 * @version 1.0.0
 * @since
 */

import xeroGuard from '@/views/platform/venue/xero/settings/xeroGuard';
import VSelectSearch from '@/components/v3/patterns/VSelectSearch/index';
import EzTable from '@/components/ui/Table/EzTable';
import EzLoadMore from '@/components/ui/LoadMore/EzLoadMore.vue';
import EzSpinner from '@/components/ui/Spinner/EzSpinner.vue';
import VVenueEntityInfo from '@/components/v3/patterns/VVenueEntityInfo/index';
import {
  venueGetVenues,
  venueGetExternalVenues,
  venueXeroAddVenue,
  venueFetchTrackingCategories,
  venueGetXeroAccounts,
  venueGetXeroTaxesSetup,
} from '@/api/endpoints/xero';
import flash from '@/components/ui/FlashMessage';
import EmptyState from '@/views/common/empty-state/EmptyState';
import EzLoader from '@/components/ui/Loader/EzLoader';
import EzCheckbox from '@/components/ui/Checkbox/Checkbox';
import FooterForUpdate from '@/views/platform/venue/xero/settings/FooterForUpdate';
import UpdateModal from '@/views/platform/venue/xero/settings/UpdateModal';
import { mapGetters } from 'vuex';
import { LOADING_KEY, SELECT_DESELECT_ALL } from '@/util/constants';
import { debounce } from '@/util/utils';
import EzFilterList from '@/components/ui/FilterList';
import EzInput from '@/components/ui/Input/EzInput.vue';

export default {
  mixins: [xeroGuard],
  components: {
    VSelectSearch,
    EzTable,
    EzLoadMore,
    EzSpinner,
    VVenueEntityInfo,
    EmptyState,
    EzLoader,
    EzCheckbox,
    FooterForUpdate,
    UpdateModal,
    EzFilterList,
    EzInput,
  },
  props: {
    venueId: {
      type: Number,
    },
  },
  data() {
    return {
      isLoaderShown: false,
      isLoadingMore: false,
      contacts: [],
      venues: [],
      tableHeaders: {
        name: this.venueNameCheckbox,
        xeroContact: () => 'Xero Contact',
      },
      meta: {},
      columns: [],
      trackingCategories: [],
      taxes: [],
      accountsData: {},
      selectedVenues: [],
      updateData: {},
      selected: false,
      listOfVenueIds: [],
      filters: {
        term: null,
      },
    };
  },
  computed: {
    ...mapGetters('loading', ['isSomeLoading']),
    isLoadingQB() {
      return this.isSomeLoading([LOADING_KEY.VENUE_UPDATE_XERO_SUPPLIERS]);
    },
    columnsNested() {
      return ['name', ...this.columns, 'xeroContact'];
    },
    entity() {
      return this.$t('global.venues');
    },
    accounts() {
      return this.accountsData?.accounts?.length;
    },
    checkedVenues() {
      return this.selectedVenues.filter(sv => sv.checked);
    },
    uncheckedVenues() {
      return this.selectedVenues.filter(sv => !sv.checked);
    },
    checkedVenuesCount() {
      return this.selected
        ? this.venues.length - this.listOfVenueIds.length
        : this.listOfVenueIds.length;
    },
    indeterminate() {
      return this.checkedVenuesCount > 0 && this.checkedVenuesCount !== this.venues.length;
    },
    areAllSelected() {
      return this.checkedVenuesCount === this.venues.length;
    },
    venuesToUpdate() {
      const venues =
        this.selected && this.filters.term === null ? this.uncheckedVenues : this.checkedVenues;
      return venues.map(vn => ({
        groupVenueId: vn.venueRowId,
      }));
    },
    isLoading() {
      return this.isLoaderShown || this.isLoadingQB;
    },
  },
  methods: {
    openUpdateModal() {
      this.$refs.updateModal.open();
    },
    vendorSelected(contact, venueId) {
      this.saveChanges({
        id: venueId,
        contactId: contact.xeroId ? contact.xeroId : null,
      });
    },
    async saveChanges(item) {
      try {
        this.isLoaderShown = true;
        await venueXeroAddVenue(this.venueId, {
          items: [
            {
              externalId: item.contactId,
              groupVenueId: item.id,
            },
          ],
        });
        const index = this.venues.findIndex(venue => venue.id === item.id);
        if (item.contactId) {
          this.venues[index].integrationEntity = {
            id: item.contactId,
            name: item.contactName,
          };
        } else {
          this.venues[index].integrationEntity = null;
        }
        this.isLoaderShown = false;
        flash.success({
          title: this.$t('quickBooks.settings.venues.flashMessages.success.title'),
        });
      } catch (e) {
        this.isLoaderShown = false;
        flash.error({
          title: this.$t('quickBooks.settings.venues.flashMessages.error.title'),
        });
      }
    },
    isChecked(row) {
      return this.selectedVenues?.find(sp => sp.venueRowId === row.id)?.checked;
    },
    getTrackingValue(item, col) {
      return item?.integrationConfig?.tracking?.[col] || '-';
    },
    venueNameCheckbox(h) {
      return h('span', { class: 'u-flex-center select-all-checkbox' }, [
        h(
          'ez-checkbox',
          {
            class: 'cursor-pointer',
            props: {
              indeterminate: this.indeterminate,
              label: `${this.$t('global.venue')}`,
              checked: this.areAllSelected,
            },
            on: {
              change: ev => {
                this.selectDeselectAll(ev);
              },
            },
          },
          [],
        ),
      ]);
    },
    selectDeselectAll(ev) {
      this.selected = ev;
      this.listOfVenueIds = [];
      this.selectedVenues = this.selectedVenues.map(cv => ({ ...cv, checked: ev }));
    },
    onCheckboxChange(pr, checked) {
      if (this.selected !== checked) this.listOfVenueIds.push({ venueRowId: pr.id });
      else this.listOfVenueIds = this.listOfVenueIds.filter(item => item.venueRowId !== pr.id);
      this.selectedVenues.find(pro => pro.venueRowId === pr.id).checked = checked;
    },
    async getTrackingCategories() {
      const {
        data: { data },
      } = await venueFetchTrackingCategories(this.venueId);
      this.trackingCategories = data;
    },
    async getAccounts() {
      const {
        data: { data: accounts },
      } = await venueGetXeroAccounts(this.venueId, true);
      this.accountsData = { accounts };

      const {
        data: { data: taxes },
      } = await venueGetXeroTaxesSetup(this.venueId, { withDefault: true });
      this.taxes = taxes;
    },
    async getVenuesListAndContacts() {
      this.isLoaderShown = true;
      const { data: contacts } = await venueGetExternalVenues(this.venueId);
      this.contacts = contacts.data;

      const { data: venues } = await venueGetVenues({
        id: this.venueId,
        ...this.filters,
      });
      this.meta = venues.meta;
      this.venues = venues.data;
      this.columns = venues.meta.columns ?? [];

      this.venues.forEach(item => {
        if (this.selectedVenues.findIndex(x => x.venueRowId === item.id) === -1) {
          this.selectedVenues.push({
            ...item,
            venueRowId: item.id,
            checked: this.selected,
          });
        }
      });

      this.isLoaderShown = false;
      this.contacts = [
        ...this.contacts,
        ...this.venues
          .filter(pair => pair.integrationEntity !== null)
          .map(item => ({ xeroId: item.integrationEntity.id, name: item.integrationEntity.name })),
      ];
      this.contacts = this.contacts.reduce((unique, o) => {
        if (!unique.some(obj => obj.xeroId === o.xeroId)) {
          unique.push(o);
        }
        return unique;
      }, []);
    },
    async fetchMoreVenues() {
      this.isLoadingMore = true;
      const { data: venues } = await venueGetVenues({
        id: this.venueId,
        ...this.meta,
        ...this.filters,
      });
      this.venues = [...this.venues, ...venues.data];
      this.meta = venues.meta;
      venues.data.forEach(item => {
        if (this.selectedVenues.findIndex(x => x.venueRowId === item.id) === -1) {
          this.selectedVenues.push({
            ...item,
            venueRowId: item.id,
            checked: this.selected,
          });
        }
      });
      this.isLoadingMore = false;
    },
    async updateVenues(ev) {
      this.config = ev;
      this.updateData = {
        ...this.updateData,
        config: this.config,
        state:
          this.selected && this.filters.term === null
            ? SELECT_DESELECT_ALL.selectedAll
            : SELECT_DESELECT_ALL.deselectedAll,
      };
      await venueXeroAddVenue(
        this.venueId,
        this.updateData,
        this.filters.term,
        undefined
      );
      await this.getVenuesListAndContacts();
      this.$refs.updateModal.close();
    },
    async resetFilters() {
      Object.keys(this.filters).forEach(key => {
        this.filters[key] = null;
      });
      this.selectDeselectAll(false);
      this.selectedVenues = [];
      await this.getVenuesListAndContacts();
    },
    updateFilters: debounce(async function deb(filterName, event) {
      if (filterName === 'search') {
        if (event.length !== 0 && event.length < 3) return;
        this.filters = { ...this.filters, term: event.length ? event : null };
      }
      this.selectDeselectAll(false);
      this.selectedVenues = [];
      await this.getVenuesListAndContacts();
    }, 300),
  },
  created() {
    this.getVenuesListAndContacts();
    this.getTrackingCategories();
    this.getAccounts();
  },
  watch: {
    venuesToUpdate() {
      this.updateData = {
        ...this.updateData,
        items: this.venuesToUpdate,
      };
    },
    checkedVenues(val) {
      window.Intercom('update', {
        hide_default_launcher: !!val.length,
      });
    },
  },
};
</script>
<template>
  <div>
    <ez-filter-list
      :filters="filters"
      @filterUpdated="updateFilters"
      @resetFilter="resetFilters"
      class="mb-24"
    >
      <ez-input
        formKey="filters"
        name="search"
        class="search"
        :placeholder="`Search for a ${$t('global.venue')}`"
      >
        <template #prefix>
          <font-awesome-icon icon="search" />
        </template>
      </ez-input>
    </ez-filter-list>
    <div>
      <ez-table
        v-if="venues.length"
        class="sync-table"
        :disable-hover="true"
        :data="venues"
        :columns="columnsNested"
        :headers="tableHeaders"
        :columnProps="{
          name: { class: 'supplier-cell' },
          xeroContact: { class: 'xxl-cell' },
        }"
      >
        <template #cell-name="{ row }">
          <div class="supplier-name">
            <ez-checkbox
              class="cursor-pointer mr-8"
              :key="row.id"
              @change="onCheckboxChange(row, $event)"
              :checked="isChecked(row)"
            />
            <v-venue-entity-info
              class="cursor-pointer"
              :venue="row"
              @click.native="onCheckboxChange(row, !isChecked(row))"
            />
          </div>
        </template>
        <template v-for="cl in columns" #[`cell-${cl}`]="{ row }">
          {{ getTrackingValue(row, cl) }}
        </template>
        <template #cell-xeroContact="{ row }">
          <v-select-search
            :data="contacts"
            :search="`/venues/${venueId}/xero/external-venues`"
            value-property="xeroId"
            placeholder="Select Xero Contact"
            :selected="row.integrationEntity?.id ?? null"
            @selected="obj => vendorSelected(obj, row.id)"
          />
        </template>
      </ez-table>
      <template v-if="!venues.length">
        <empty-state>
          <template #badge>
            <img src="@/assets/no-venue-empty-state.svg" width="160" alt="" />
          </template>
          <template #title>{{ $t('xero.settings.venues.emptyState.title') }}</template>
        </empty-state>
      </template>
      <div v-if="isLoadingMore" class="u-text-center mt-12">
        <ez-spinner />
      </div>
      <ez-load-more
        v-if="meta.nextId && meta.nextValue && !isLoadingMore"
        @loadMore="fetchMoreVenues"
      />
      <update-modal
        v-if="accounts"
        ref="updateModal"
        :entity="entity"
        :categories="trackingCategories"
        :acc-data="accountsData"
        @update="updateVenues"
        :taxes="taxes"
        :show-tax-rate="true"
      />
      <footer-for-update
        v-if="checkedVenuesCount"
        :items-count="checkedVenuesCount"
        :entity="entity"
        @footerAction="openUpdateModal"
      />
      <ez-loader :show="isLoading">Loading...</ez-loader>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.sync-table tbody tr td {
  overflow: visible;
}
.supplier-name {
  display: flex;
  align-items: center;
}
.supplier-cell {
  width: 500px;
}
.select-all-checkbox {
  .input-group {
    @include font-size(12px);
    :deep() .check-indicator {
      margin-right: 8px;
    }
  }
}
.header {
  width: 400px;
  &__info-text {
    @include font-size(14px, 20px);
    font-weight: 500;
    color: $color-gray-6C;
  }
}

:deep() .ez-filter-list__items .search {
  width: 260px;
}

:deep() .select-search__trigger {
  min-width: 220px;
}
</style>
